/**
 * Admin sign-in — SPEC §9.4.
 *
 * Google Identity Services renders its own button in **redirect mode**: clicking it navigates
 * to Google, and Google posts the credential to our server, which verifies it (see
 * `services/google-identity.ts`) and redirects back here signed in.
 *
 * ## Why not the popup
 *
 * The popup flow failed silently in ordinary Chrome — the account chooser appeared, an account
 * was picked, and the popup went blank with no callback and nothing reaching the server. That
 * is third-party-storage restrictions breaking GSI's postMessage back to the opener, and it is
 * not fixable from our side. Redirect mode uses no popup and no third-party cookies at all.
 *
 * ## Why the script is loaded here rather than in index.html
 *
 * Thirty instructors load the portal; two people load this page. Putting Google's script in
 * the document head would make every instructor fetch a third-party script on a phone, on
 * every visit, for a button they will never see — and it would widen the CSP for the whole
 * app rather than for one route.
 *
 * ## What this page must never do
 *
 * Say whether an address is an admin. A colleague who signs in with a valid
 * `@gearupwithcpr.com` account and is not on the allowlist gets the same message as anyone
 * else: ask Luke. Anything more precise turns this into a directory of who has access.
 */

import { useEffect, useRef, useState } from 'react';
import { adminApi } from '../../lib/api.js';

const GSI_SRC = 'https://accounts.google.com/gsi/client';

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string;
            callback?: (response: { credential?: string }) => void;
            ux_mode?: 'popup' | 'redirect';
            login_uri?: string;
            hd?: string;
            auto_select?: boolean;
            cancel_on_tap_outside?: boolean;
          }) => void;
          renderButton: (parent: HTMLElement, options: Record<string, unknown>) => void;
        };
      };
    };
  }
}

/** Load the GSI script once, and resolve when it is actually usable. */
function loadGoogleScript(): Promise<void> {
  if (window.google?.accounts?.id) return Promise.resolve();

  const existing = document.querySelector<HTMLScriptElement>(`script[src="${GSI_SRC}"]`);
  if (existing) {
    return new Promise((resolve, reject) => {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('script failed')));
    });
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = GSI_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('script failed'));
    document.head.appendChild(script);
  });
}

/**
 * No `onSignedIn` callback: in redirect mode the browser leaves this page entirely and comes
 * back with a session cookie, so the shell re-fetches `/me` on mount. A callback here would
 * be dead code pretending the sign-in completes in place.
 */
export function AdminSignIn() {
  const buttonRef = useRef<HTMLDivElement>(null);
  const [domain, setDomain] = useState('gearupwithcpr.com');
  // Read inside messageFor, which is created before the fetch resolves.
  const domainRef = useRef('gearupwithcpr.com');
  const [status, setStatus] = useState<'loading' | 'ready' | 'unavailable'>('loading');
  const [error, setError] = useState<string | null>(null);

  /** Whatever Google or our callback rejected, restated for a person. */
  const messageFor = (reason: string): string =>
    ({
      csrf: 'That sign-in could not be verified. Please try again.',
      wrong_domain: `Admin access is limited to @${domainRef.current} accounts.`,
      not_an_admin: 'That account is not set up for the dashboard. Ask Luke to add you.',
      deactivated: 'That admin account has been deactivated.',
      not_configured: 'Google sign-in is not configured on this server yet.',
    })[reason] ?? 'That sign-in did not work. Please try again.';

  useEffect(() => {
    const reason = new URLSearchParams(window.location.search).get('error');
    if (reason) {
      setError(messageFor(reason));
      // Clear it, so a reload does not re-show a stale failure.
      window.history.replaceState({}, '', '/admin');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let canceled = false;

    void (async () => {
      try {
        const config = await adminApi.config();
        if (canceled) return;
        setDomain(config.domain);
        domainRef.current = config.domain;

        if (!config.googleClientId) {
          // A missing client ID is a deployment problem, not a user error, and saying so
          // saves somebody hunting for a button that was never going to appear.
          setStatus('unavailable');
          setError('Google sign-in is not configured on this server yet.');
          return;
        }

        await loadGoogleScript();
        if (canceled || !window.google?.accounts?.id || !buttonRef.current) return;

        window.google.accounts.id.initialize({
          client_id: config.googleClientId,
          ux_mode: 'redirect',
          // Must match an Authorized redirect URI exactly. Built from the live origin so
          // localhost, staging and production each point at themselves.
          login_uri: `${window.location.origin}/api/admin/google-callback`,
          // A hint to Google's account chooser, never a security control — the server
          // re-checks `hd` on the token it receives.
          hd: config.domain,
          auto_select: false,
        });

        window.google.accounts.id.renderButton(buttonRef.current, {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          text: 'signin_with',
          shape: 'rectangular',
          logo_alignment: 'left',
          width: 280,
        });
        setStatus('ready');
      } catch {
        if (canceled) return;
        setStatus('unavailable');
        setError("Couldn't reach Google to set up sign-in. Check your connection and reload.");
      }
    })();

    return () => {
      canceled = true;
    };
  }, []);

  return (
    <div className="card stack" style={{ maxWidth: 420, margin: '0 auto' }}>
      <h1 style={{ margin: 0 }}>Dashboard</h1>
      <p className="muted" style={{ margin: 0 }}>
        Sign in with your <strong>@{domain}</strong> Google account.
      </p>

      {error && (
        <div className="notice notice--error" role="alert">
          <span className="notice__icon" aria-hidden="true">!</span>
          <span>{error}</span>
        </div>
      )}

      {/* Google renders into this node. Kept mounted in every state so the ref exists by
          the time the script is ready. */}
      <div
        ref={buttonRef}
        style={{ display: 'flex', justifyContent: 'center', minHeight: 44 }}
        aria-busy={status === 'loading'}
      />

      {status === 'loading' && <p className="small muted center">Setting up sign-in…</p>}

      <p className="small muted center" style={{ margin: 0 }}>
        This is the office dashboard. Instructors sign in at{' '}
        <a href="/">the main portal</a>.
      </p>
    </div>
  );
}
